/**
 * The request body for `POST /home_run_harvest {action:"record"}` — the ONLY call in the
 * entire app that starts a 60-calendar-day redeployment lockout on harvested capital.
 * There is no backend timer behind it and no other caller.
 *
 * Extracted as a pure function so the contract can be PINNED BY TEST rather than verified
 * by executing a real trim. The Signals implementation replaced the Portfolio one, and a
 * real 2x candidate may not appear for months — so "run one and see" was not a workable
 * gate. See harvestRecord.test.ts.
 *
 * WHY returnPct IS DELIBERATELY OMITTED.
 * The backend does this (strategy/home_run_redeployment.py):
 *
 *     return_pct = _normalize_return(payload.get("returnPct"))
 *     if return_pct is None and entry_price and entry_price > 0:
 *         return_pct = (fill_price - entry_price) / entry_price
 *     ...
 *     if return_pct is not None and return_pct < trigger_return:
 *         warnings.append("Recorded return is below the configured home-run trigger...")
 *
 * If we send a returnPct, the backend BELIEVES IT — for the permanent record and for the
 * below-trigger warning. The candidate's returnPct is computed from `market_price`, which
 * is a MARK (the bid historically; the mid since 2026-08-27), not the price you sold at.
 * Sending it would archive a mark-based return as the realized one and suppress the
 * below-trigger warning on a trim that was under 2x at the actual fill — an error in the
 * flattering direction, permanently, in an audit record.
 *
 * Omitting it makes the backend derive the return from the REAL fill price the CIO typed.
 * `entryPrice` is still sent (and falls back to the stored position) so it can.
 */

export type HarvestRecordInput = {
  contractId: string;
  symbol?: string | null;
  accountId?: string | null;
  displayName?: string | null;
  /** Contracts held before the trim. */
  originalQuantity?: number | null;
  /** Contracts actually sold in TWS. */
  executedQty: number;
  /** Average fill price per contract, from TWS. */
  fillPrice: number;
  entryPrice?: number | null;
  recordedBy?: string | null;
  notes?: string;
};

export type HarvestRecordBody = {
  action: "record";
  contractId: string;
  symbol: string;
  accountId: string | null;
  displayName: string;
  originalQuantity: number | null;
  executedQty: number;
  fillPrice: number;
  entryPrice: number | null;
  recordedBy: string;
  notes: string;
};

const posNum = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;

/**
 * Throws rather than sending a request the backend will reject — the three fields it
 * hard-requires are validated here so a malformed record cannot reach the lockout path.
 */
export function buildHarvestRecordBody(input: HarvestRecordInput): HarvestRecordBody {
  const contractId = String(input.contractId ?? "").trim();
  if (!contractId) throw new Error("contractId is required.");

  const executedQty = posNum(input.executedQty);
  if (executedQty === null || !Number.isInteger(executedQty)) {
    throw new Error("executedQty must be a positive whole number of contracts.");
  }

  const fillPrice = posNum(input.fillPrice);
  if (fillPrice === null) throw new Error("fillPrice must be a positive number.");

  return {
    action: "record",
    contractId,
    symbol: input.symbol ?? "SPX",
    accountId: input.accountId ?? null,
    displayName: input.displayName ?? contractId,
    originalQuantity: posNum(input.originalQuantity),
    executedQty,
    fillPrice,
    entryPrice: posNum(input.entryPrice),
    recordedBy: input.recordedBy || "CIO",
    notes: input.notes ?? "Manual TWS execution recorded from the Signals harvest panel.",
    // returnPct intentionally absent — see the file header.
  };
}
